import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  TrendingDown,
  Share2,
  Compass,
  FileText,
  Search,
  Phone,
  Sparkles,
  CheckCircle2,
  ShieldCheck,
  PlayCircle,
  Mail,
  MessageSquare,
  Heart,
  ShoppingBag,
  BarChart3,
  Crown,
  Briefcase,
  Star,
  RefreshCw,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';
import ClientLogosWall from '../../components/sections/ClientLogosWall';
import CandidateTestimonial from '../../components/sections/CandidateTestimonial';
import WhitepaperModal from '../../components/whitepaper/WhitepaperModal';
import { useWhitepaperPrompt } from '../../hooks/useWhitepaperPrompt';
import { CHIFFRES_CLES_RECRUTEMENT } from '../../lib/whitepapers';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// — Typography aligned on home-page.tsx ————————————————————————
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass =
  'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass = 'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — 3 cartes chiffrées sur la marque employeur en jeu ——————
const brandRiskStats = [
  {
    icon: Share2,
    metric: '72 %',
    title: 'partagent leur expérience candidat',
    description:
      "Chaque candidat de votre process en parle. À ses pairs, à ses collègues, sur LinkedIn, sur Glassdoor. Que vous l'embauchiez ou non. Source : Talent Board.",
  },
  {
    icon: TrendingDown,
    metric: '64 %',
    title: 'arrêtent d’acheter chez la marque',
    description:
      "Les candidats déçus ne vous oublient pas — ils boycottent. Pour une marque B2C (retail, restauration, services, télécom, banque), le coût peut atteindre plusieurs millions d'euros par an. Source : Talent Board.",
  },
  {
    icon: AlertTriangle,
    metric: '81 %',
    title: 'blacklistent une marque qui les ghoste',
    description:
      "Une candidature sans réponse, un process qui s'éteint en silence : et c'est un client, un futur candidat, et son réseau qui partent définitivement. Source : étude marché 2024.",
  },
];

// — Section 3 — Timeline 7 étapes ————————————————————————————————————
const methodSteps = [
  {
    icon: FileText,
    title: 'Cadrage business du besoin',
    description:
      "Pas une fiche de poste générique : un diagnostic d'enjeu. Pourquoi ce poste, pour quel résultat, dans quel contexte managérial, avec quels critères de réussite mesurables.",
  },
  {
    icon: Compass,
    title: 'Réécriture engageante du brief candidat',
    description:
      "Nous transformons votre fiche de poste en mission claire, honnête, attirante. Le candidat doit comprendre POURQUOI ce poste vaut son temps avant de comprendre quoi y faire.",
  },
  {
    icon: Search,
    title: 'Sourcing chasse de tête + IA',
    description:
      "Approche directe sur les profils correspondants, scoring sémantique IA pour élargir intelligemment, qualification fine. La chasse senior pilote, l'IA accélère.",
  },
  {
    icon: Phone,
    title: 'Pré-qualification soignée',
    description:
      "Chaque candidat reçoit un appel de pré-qualification respectueux et structuré, en 30 minutes. C'est souvent leur premier contact avec votre entreprise — nous le traitons comme tel.",
  },
  {
    icon: Sparkles,
    title: 'Parcours Découverte avant la décision',
    description:
      "Les candidats short-listés traversent votre Parcours Découverte. Ils découvrent votre culture, votre équipe, votre quotidien réel, votre rémunération. Ils décident en connaissance de cause.",
  },
  {
    icon: CheckCircle2,
    title: 'Signature et ré-engagement',
    description:
      "Le candidat retenu se ré-engage explicitement à la sortie du Parcours. Plus de signature à l'aveugle, plus de no-show au jour J, plus de rétractation au dernier moment.",
  },
  {
    icon: ShieldCheck,
    title: 'Intégration et suivi 90 jours',
    description:
      "Onboarding personnalisé dès la signature, points de feedback RH et manager, objectifs suivis semaine par semaine. Le recrutement n'est solide qu'au 90e jour — pas à la signature.",
  },
];

// — Section 4 — 4 engagements expérience candidat ————————————————————
const candidateCommitments = [
  {
    icon: Mail,
    title: 'Réponse écrite à 100 % des candidatures',
    description:
      "Sous 48 heures ouvrées. Aucun ghosting, jamais. Même un refus est une réponse soignée — c'est ce qui fait la différence dans la mémoire des candidats.",
  },
  {
    icon: MessageSquare,
    title: 'Brief honnête sur le poste, l’équipe, le contexte',
    description:
      "Pas de marketing maquillé, pas de promesse vague. Le candidat reçoit la vraie réalité — y compris les contraintes — pour pouvoir décider en adulte.",
  },
  {
    icon: RefreshCw,
    title: 'Debrief structuré à chaque étape',
    description:
      "Le candidat sait pourquoi il avance, ou pourquoi il s'arrête. Avec des éléments concrets. Pour qu'il progresse, même s'il n'est pas retenu chez vous.",
  },
  {
    icon: Sparkles,
    title: 'Parcours Découverte avant l’entretien final',
    description:
      "Pas de surprise après la signature, plus de regret six mois après. Le candidat voit la réalité avant de s'engager — et n'avance que s'il est aligné.",
  },
];

// — Section 5 — Double dividende stats ————————————————————————————————
const dividendStats = [
  {
    icon: Heart,
    metric: '88 %',
    text:
      "des candidats avec une bonne expérience deviennent acheteurs des produits ou services — y compris s'ils n'ont pas été retenus. Source : Talent Board.",
  },
  {
    icon: ShoppingBag,
    metric: '× 4,4 M £',
    text:
      "C'est ce que Virgin Media a chiffré comme revenu perdu chaque année à cause de ses candidats refusés qui résiliaient leur abonnement.",
  },
  {
    icon: BarChart3,
    metric: '91 % vs 26 %',
    text:
      "des employeurs admettent l'impact de l'expérience candidat sur leurs ventes — 26 % seulement la mesurent. Le gap est votre opportunité.",
  },
];

// — Section 6 — Pour qui ————————————————————————————————————————————
const fitProfiles = [
  {
    icon: Crown,
    title: 'Vous remplacez un cadre clé ou un dirigeant',
    description:
      "Le poste qui ne supporte pas un faux pas. Là où chaque jour de vacance coûte, et où chaque erreur de casting coûte plus cher encore.",
  },
  {
    icon: Briefcase,
    title: 'Vous embauchez sur un poste pivot',
    description:
      "Commercial senior, manager, expert métier. Un poste qui structure une équipe, ouvre un marché, lance un produit.",
  },
  {
    icon: Star,
    title: 'Vous recrutez sur un profil rare ou tendu',
    description:
      "IT, ingénieurs, profils experts difficiles à attirer. Là où l'image employeur fait la différence entre un oui et un non.",
  },
  {
    icon: Heart,
    title: 'Vous tenez à votre culture',
    description:
      "Votre équipe a une cohésion forte que vous ne voulez pas casser. L'erreur de mismatch culturel vous coûte plus que l'erreur de compétence.",
  },
];

// — Section 7 — Garantie ————————————————————————————————————————————
const guaranteeBullets = [
  {
    icon: RefreshCw,
    title: 'Replacement gratuit en cas de rupture < 6 mois',
    description:
      "Si votre nouvelle recrue rompt son contrat dans les 6 premiers mois, nous relançons un sourcing complet sans frais supplémentaires.",
  },
  {
    icon: FileText,
    title: 'Diagnostic post-mortem honnête',
    description:
      "Nous identifions où ça a vraiment lâché : projection candidat, package, management, contexte business. Pour ne pas refaire la même erreur.",
  },
  {
    icon: ShieldCheck,
    title: '30 % du fee restitué',
    description:
      "Si vous ne souhaitez pas de replacement, nous vous restituons 30 % du fee. Pour que la garantie soit réelle — pas un argument commercial.",
  },
];

export default function RecrutementPage() {
  const navigate = useNavigate();
  const whitepaper = useWhitepaperPrompt({ slug: CHIFFRES_CLES_RECRUTEMENT.slug });

  return (
    <Layout
      title="Recrutement de cadres à Marseille"
      description="Recrutement de cadres à Marseille, Aix, PACA : postes pourvus en 6 semaines. Chasse + IA + Parcours Découverte, garantie 6 mois écrite. Brief gratuit."
      keywords="cabinet de recrutement marseille, recrutement cadre marseille, recrutement dirigeant marseille, chasse de tête paca, mission de recrutement, parcours découverte, garantie recrutement, recrutement aix"
    >
      <WebPageSchema
        page={{
          title: 'Recrutement de cadres à Marseille | Recruteurs.io',
          description:
            "recruteurs.io traite chaque recrutement à l’unité comme une rencontre avec votre marque. Méthode en 7 temps, Plateforme Parcours Découverte, garantie replacement, suivi 90 jours.",
          url: 'https://recruteurs.io/entreprise/recrutement',
          lastReviewed: '2026-05-09',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Entreprise', url: 'https://recruteurs.io/entreprise/recrutement' },
          { name: 'Recrutement', url: 'https://recruteurs.io/entreprise/recrutement' },
        ]}
      />

      {/* HERO */}
      <HeroTemplateEnts
        page="recrutement"
        title="Le bon recrutement commence par le bon premier contact."
        eyebrow="Recrutement à l’unité · Poste critique · Marseille · Aix · PACA"
        description="Vous avez un poste critique à pourvoir. Pas une campagne, pas un volume — un seul recrutement, mais qui compte. Pour vous, c’est une décision business. Pour chaque candidat qui passera par votre process, c’est une rencontre avec votre marque. recruteurs.io traite les deux avec la même exigence."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Recrutement-de-cadres-marseille.webp"
        breadcrumbs={[
          { name: 'Entreprise', href: '/entreprise/recrutement' },
          { name: 'Recrutement', href: '/entreprise/recrutement' },
        ]}
        cta1={{
          label: 'Réserver un échange (15 min)',
          onClick: () => navigate('/reservation'),
        }}
        cta2={{
          label: 'Voir le Parcours Découverte',
          onClick: () => navigate('/decouvrir'),
        }}
      />

      {/* SECTION 2 — Le constat : 80 candidats, 1 embauche, 79 témoins */}
      <SectionBackground page="recrutement" section="recrutement-stake" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le vrai coût d’un recrutement mal traité</span>

            <h2 className={h2Class}>
              Pour vous, c’est un poste à pourvoir.{' '}
              <span className={gradientText}>
                Pour 80 candidats, c’est leur seul contact avec votre marque.
              </span>
            </h2>

            <p className={sectionSubtitleClass}>
              Sur un poste cadre, vous allez recevoir entre 50 et 100 candidatures.
              Vous en embaucherez une. Les autres ne disparaissent pas dans le silence —
              ils retournent à leur réseau, à leurs collègues, à LinkedIn, à Glassdoor.
              Avec, en mémoire, l’expérience qu’ils viennent de vivre. Bonne ou mauvaise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {brandRiskStats.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-7 h-7" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                      {item.metric}
                    </p>
                  </div>

                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Méthode 7 étapes */}
      <SectionBackground page="recrutement" section="recrutement-method" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre méthode en 7 temps</span>

            <h2 className={h2Class}>
              Exigeante côté business,{' '}
              <span className={gradientText}>premium côté candidat.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Sept étapes qui s’enchaînent — du diagnostic d’enjeu jusqu’à l’ancrage
              de la prise de poste au 90&shy;e jour. Chaque étape est pensée pour
              sécuriser votre embauche ET protéger votre marque employeur.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methodSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.07 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>

                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Engagements expérience candidat */}
      <SectionBackground page="recrutement" section="recrutement-cx" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce que vivent vos candidats</span>

            <h2 className={h2Class}>
              Tous vos candidats, retenus ou non, repartent avec{' '}
              <span className={gradientText}>votre histoire bien racontée.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Quatre engagements concrets, vérifiables, opposables. Pas du discours.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {candidateCommitments.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Double dividende */}
      <SectionBackground page="recrutement" section="recrutement-dividend" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le bénéfice qui paie deux fois</span>

            <h2 className={h2Class}>
              Vous embauchez 1 candidat.{' '}
              <span className={gradientText}>Vous gagnez 79 ambassadeurs.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Sur un poste, 80 candidatures, 1 embauche. Les 79 autres ne disparaissent
              pas — ils parlent. À leurs collègues, sur LinkedIn, à leur prochain
              entretien. Ce qu’ils diront de vous dépend exclusivement de la qualité
              du process qu’ils viennent de vivre.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {dividendStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.metric}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                      {stat.metric}
                    </p>
                  </div>
                  <p className={bodyClass}>{stat.text}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-4xl mx-auto">
            <p className={`${bodyClass} text-center`}>
              recruteurs.io vous garantit que ce sera une bonne histoire — celle que
              <strong> vous</strong> avez choisi de raconter. Pas une rumeur de couloir,
              pas un avis Glassdoor frustré.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Pour qui */}
      <SectionBackground page="recrutement" section="recrutement-fit" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pour qui</span>

            <h2 className={h2Class}>
              Le recrutement à l’unité recruteurs.io est fait pour vous{' '}
              <span className={gradientText}>si&hellip;</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fitProfiles.map((profile, index) => {
              const Icon = profile.icon;
              return (
                <motion.div
                  key={profile.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{profile.title}</h3>
                  <p className={bodyClass}>{profile.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — Garantie */}
      <SectionBackground page="recrutement" section="recrutement-guarantee" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre garantie</span>

            <h2 className={h2Class}>
              Si ça ne tient pas dans les 6 mois,{' '}
              <span className={gradientText}>on recommence.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Une garantie chiffrée, opposable. Pour que l’engagement soit réel —
              pas un argument commercial.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {guaranteeBullets.map((g, index) => {
              const Icon = g.icon;
              return (
                <motion.div
                  key={g.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{g.title}</h3>
                  <p className={bodyClass}>{g.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 bis — Témoignage candidat (preuve sociale Parcours Découverte) */}
      <CandidateTestimonial
        eyebrow="Témoignage candidat"
        sectionTitle={
          <>
            Ce que les candidats nous{' '}
            <span className={gradientText}>disent réellement.</span>
          </>
        }
        name="Benoit"
        role="Commercial"
        verified
        quote="Une **expérience candidat** vraiment bien pensée ! Toutes les informations que l'on cherche habituellement un peu partout sont **enfin accessibles au même endroit**. J'ai même reconnu une ancienne collègue dans la vidéo, ce qui a rendu l'**expérience encore plus engageante** !"
        background="white"
      />

      {/* SECTION 8 — Logos clients (wall créatif partagé) */}
      <ClientLogosWall page="recrutement" section="recrutement-trust" />

      {/* SECTION 9 — Vu à la télé */}
      <SectionBackground page="recrutement" section="recrutement-press" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>
              <PlayCircle className="w-4 h-4 mr-2" />
              Vu à la télé
            </span>

            <h2 className={h2Class}>
              Notre vision du recrutement,{' '}
              <span className={gradientText}>expliquée à l’antenne.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Notre équipe, notre méthode, notre conviction sur le recrutement
              moderne — racontées en quelques minutes.
            </p>
          </div>

          <div className="max-w-4xl mx-auto rounded-[2rem] overflow-hidden ring-1 ring-neutral-200 shadow-xl">
            <div
              className="relative w-full"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://www.youtube.com/embed/sXY1BChmGSc"
                title="recruteurs.io passe à la télé — vision du recrutement"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 10 — CTA final */}
      <SectionBackground page="recrutement" section="recrutement-cta" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Parlons de votre poste critique.{' '}
              <span className={gradientText}>15 minutes, sans engagement.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Nous qualifions ensemble votre poste, votre enjeu, votre contexte.
              Si recruteurs.io est la bonne solution, nous chiffrons. Si non,
              nous vous orientons. Pas de blabla, pas de brochure.
            </p>

            <button
              onClick={() => navigate('/reservation')}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Réserver un échange (15 min)
            </button>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 11 — Bloc SEO de fin (préservé et retravaillé) */}
      <SectionBackground page="recrutement" section="recrutement-seo" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto">
            <h2 className={`${h2Class} text-center`}>
              Cabinet de recrutement à Marseille,{' '}
              <span className={gradientText}>Aix-en-Provence et PACA.</span>
            </h2>

            <div className="space-y-5 text-base md:text-lg text-neutral-700 leading-relaxed mt-10">
              <p>
                recruteurs.io est un cabinet de recrutement implanté à{' '}
                <a href="/cabinet-recrutement-marseille" className="underline hover:text-[#d03484]">Marseille</a>,
                qui intervient sur l’ensemble des Bouches-du-Rhône et de la{' '}
                <a href="/cabinet-recrutement-paca" className="underline hover:text-[#d03484]">PACA</a> :{' '}
                <a href="/cabinet-recrutement-aix-en-provence" className="underline hover:text-[#d03484]">Aix-en-Provence</a>,
                le Pôle d’Activités, Vitrolles, Marseille
                Euroméditerranée, La Joliette, le Prado, et toute la couronne.
              </p>
              <p>
                Notre périmètre couvre les <strong>postes cadres et dirigeants</strong>,
                les fonctions commerciales, marketing, IT, industrie, retail et
                services. De la{' '}
                <a href="/entreprise/recrutement-dirigeants" className="underline hover:text-[#d03484]"><strong>chasse de tête senior</strong></a>{' '}
                au recrutement direct, en passant par les{' '}
                <a href="/entreprise/campagnes" className="underline hover:text-[#d03484]">
                  campagnes de recrutement à fort volume
                </a>
                {' '}et l’
                <a href="/entreprise/externalisation" className="underline hover:text-[#d03484]">
                  externalisation RH
                </a>.
              </p>
              <p>
                Notre signature&nbsp;: la{' '}
                <a href="/decouvrir" className="underline hover:text-[#d03484]">
                  Plateforme Parcours Découverte
                </a>{' '}
                — un environnement digital sur-mesure que vos candidats traversent
                avant de signer. Ils comprennent vraiment votre culture, votre
                équipe, votre rémunération. Ils décident en connaissance de cause.
                Et ils tiennent.
              </p>
              <p>
                Notre modèle financier est{' '}
                <a href="/infos/tarifs" className="underline hover:text-[#d03484]">
                  transparent et adaptable
                </a>{' '}
                — selon que vous préférez engager un fee retainer ou
                travailler exclusivement au succès.
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* Modale livre blanc — exit-intent / scroll 60% / fallback 30s, 1 fois max */}
      <WhitepaperModal
        open={whitepaper.isOpen}
        onClose={whitepaper.close}
        onDownloaded={whitepaper.markDownloaded}
        paper={CHIFFRES_CLES_RECRUTEMENT}
      />
    </Layout>
  );
}
